import { useState, useId, useRef, useEffect, type RefObject } from "react";
import { Mail, Check, X } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import {
  SUBSCRIBED_LOCAL_STORAGE_KEY,
  validateEmail,
  type CohortKey,
} from "@/utils/constants";
import { getAcquisition, type SignupWidget } from "@/utils/acquisition";
import { identifyEmail, useTrack } from "@/analytics/events";
import { trackPinterest } from "@/analytics/pinterest";
import { subscribeEmail } from "@/lib/keepMePosted";

const subscribeInputVariants = cva("flex w-full items-center justify-between", {
  variants: {
    variant: {
      underline: "",
      boxed: "rounded-lg border border-[#dbdbdb] px-4 py-3",
    },
  },
  defaultVariants: {
    variant: "underline",
  },
});

type SubscribeInputProps = {
  source: SignupWidget;
  onSuccess?: () => void;
  selectedOptions?: CohortKey[];
  inputRef?: RefObject<HTMLInputElement>;
  inputClassName?: string;
  arrowClassName?: string;
} & VariantProps<typeof subscribeInputVariants>;

export default function SubscribeInput({
  source,
  onSuccess,
  selectedOptions = [],
  inputRef,
  inputClassName,
  arrowClassName,
  variant = "underline",
}: SubscribeInputProps) {
  const [email, setEmail] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [validationError, setValidationError] = useState("");
  const [hasError, setHasError] = useState(false);
  const errorId = useId();
  const track = useTrack();
  const successTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const errorTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(
    () => () => {
      clearTimeout(successTimeout.current);
      clearTimeout(errorTimeout.current);
    },
    [],
  );

  const flashError = () => {
    setHasError(true);
    clearTimeout(errorTimeout.current);
    errorTimeout.current = setTimeout(() => setHasError(false), 2000);
  };

  const handleSubmit = async () => {
    const trimmed = email.trim();
    const isValid = Boolean(trimmed) && validateEmail(trimmed);
    track("email_signup_submitted", { source, is_valid: isValid });
    if (!isValid) {
      setValidationError("Enter a valid email address.");
      flashError();
      return;
    }

    setValidationError("");
    setIsProcessing(true);
    setErrMsg("");

    try {
      const result = await subscribeEmail({
        email: trimmed,
        selectedOptions,
        acquisition: getAcquisition(source),
      });

      if (result.ok) {
        try {
          localStorage.setItem(SUBSCRIBED_LOCAL_STORAGE_KEY, "true");
        } catch (error) {
          console.error("Failed to access localStorage:", error);
        }
        identifyEmail(trimmed);
        setIsProcessing(false);
        setSucceeded(true);
        track("email_signup_succeeded", { source });
        trackPinterest("lead", () => ({ lead_type: "Newsletter" }));
        successTimeout.current = setTimeout(() => {
          setSucceeded(false);
          setEmail("");
          onSuccess?.();
        }, 3000);
      } else {
        setErrMsg(result.message);
        setIsProcessing(false);
        track("email_signup_failed", { source, error_message: result.message });
      }
    } catch (error) {
      console.error("Failed to submit email signup:", error);
      setIsProcessing(false);
      setErrMsg("Something went wrong");
      track("email_signup_failed", { source, error_message: "network_error" });
    }
  };

  const boxed = variant === "boxed";
  const isIdle = !isFocused && email.length === 0;

  return (
    <div className="flex flex-col gap-[8px]">
      <div
        className={cn(
          subscribeInputVariants({ variant }),
          "rounded-sm has-[input:focus-visible]:outline has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-[#1f2022]",
        )}
      >
        <div
          className={cn(
            "flex flex-1 items-center",
            boxed ? "gap-3" : "gap-[16px]",
          )}
        >
          <Mail
            size={boxed ? 24 : 18}
            strokeWidth={boxed ? 1 : 1.5}
            className="shrink-0 text-[#1F2022]"
          />
          <input
            ref={inputRef}
            type="email"
            inputMode="email"
            autoComplete="email"
            aria-label="Email address"
            disabled={isProcessing || succeeded}
            aria-invalid={hasError || Boolean(errMsg || validationError)}
            aria-describedby={errMsg || validationError ? errorId : undefined}
            className={cn(
              "flex-1 bg-transparent font-ppMori text-[16px] tracking-[-0.32px] text-[#1F2022] caret-[#1F2022] focus:outline-none",
              boxed
                ? "placeholder-[#747474]"
                : isIdle
                  ? "placeholder-[#1F2022]"
                  : "placeholder-[#747474]",
              inputClassName,
            )}
            value={email}
            placeholder={
              boxed
                ? "Enter your email to subscribe"
                : isIdle
                  ? "Subscribe to Matic news"
                  : "Your email address"
            }
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errMsg) setErrMsg("");
              if (validationError) setValidationError("");
              if (hasError) setHasError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
          />
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isProcessing || succeeded}
          className={cn(
            "flex size-[29px] shrink-0 items-center justify-center transition-opacity",
            boxed
              ? email.length > 0
                ? "text-[#1f2022]"
                : "text-[#8d8b84]"
              : "text-[#1F2022]",
            !boxed && !succeeded && "disabled:opacity-20",
            !boxed && !succeeded && email.length === 0 && "opacity-60",
            arrowClassName,
          )}
          aria-label="Submit email"
        >
          {isProcessing ? (
            <Spinner className="size-[18px]" />
          ) : succeeded ? (
            <Check size={20} strokeWidth={1.5} />
          ) : hasError ? (
            <X size={24} strokeWidth={1.5} className="text-legacy-error" />
          ) : (
            <span className="font-ppMori text-[18px] leading-[1.6]">→</span>
          )}
        </button>
      </div>
      {!boxed && <div className="h-px w-full bg-[#1F2022]" />}
      {(errMsg || validationError) && (
        <div
          id={errorId}
          role="alert"
          className="font-ppMori text-[14px] leading-[1.6] tracking-[-0.28px] text-legacy-error"
        >
          {errMsg || validationError}
        </div>
      )}
      <div role="status" aria-live="polite" className="sr-only">
        {succeeded ? "Email subscription successful." : ""}
      </div>
    </div>
  );
}

export { subscribeInputVariants };
